import { useMemo, useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ChartPie,
  Clock3,
  DollarSign,
  Filter,
  Heart,
  HeartPulse,
  LayoutDashboard,
  Smile,
  UserPlus,
  Users,
  Wallet,
} from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { DashboardPageHeader } from "@/shared/ui/DashboardPageHeader";
import { DashboardSectionLabel } from "@/shared/ui/DashboardSectionLabel";
import { inputClass, selectClass } from "../components/FormField";
import { EnterpriseDashboardQuickAccess } from "../components/enterprise-dashboard/EnterpriseDashboardQuickAccess";
import { EnterpriseDashboardMetrics } from "../components/enterprise-dashboard/EnterpriseDashboardMetrics";
import { EnterpriseDashboardRankingCard } from "../components/enterprise-dashboard/EnterpriseDashboardRankingCard";
import { EnterpriseDashboardCareTypes } from "../components/enterprise-dashboard/EnterpriseDashboardCareTypes";
import { EnterpriseDashboardAgePyramid } from "../components/enterprise-dashboard/EnterpriseDashboardAgePyramid";
import { useEnterpriseDashboardAlerts } from "../hooks/useEnterpriseDashboardAlerts";
import { useEnterpriseDashboard } from "../hooks/useEnterpriseDashboard";
import {
  dashboardPeriodQueryString,
  parseDashboardPeriodSearchParams,
} from "../lib/dashboardPeriodQuery";
import { cn } from "@/shared/lib/cn";

function SynthesisCard({
  title,
  subtitle,
  primaryValue,
  primaryCaption,
  icon: Icon,
  children,
  cardClassName,
  iconClassName = "bg-primary-soft",
  iconColorClassName = "text-primary",
}: {
  title: string;
  subtitle?: React.ReactNode;
  primaryValue?: string;
  primaryCaption?: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  children: React.ReactNode;
  cardClassName?: string;
  iconClassName?: string;
  iconColorClassName?: string;
}) {
  return (
    <div className={cn("theme-card flex h-full cursor-pointer flex-col p-5", cardClassName)}>
      <div className="mb-4 flex min-h-[4.75rem] items-start gap-3">
        <div
          className={cn(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
            iconClassName,
          )}
        >
          <Icon size={20} className={iconColorClassName} />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold uppercase tracking-wide text-foreground">{title}</h3>
          <div className="mt-0.5 min-h-[2.5rem] space-y-0.5 text-xs text-muted">
            {subtitle ?? <span className="invisible">.</span>}
          </div>
        </div>
      </div>
      {primaryValue !== undefined && (
        <div className="mb-4 border-b border-border pb-4">
          <div className="flex h-9 items-center justify-center">
            <p className="text-center text-[36px] font-bold leading-none tabular-nums text-foreground">
              {primaryValue}
            </p>
          </div>
          <p
            className={cn(
              "mt-4 min-h-4 text-center text-xs font-medium text-muted",
              !primaryCaption && "invisible",
            )}
          >
            {primaryCaption ?? "·"}
          </p>
        </div>
      )}
      <div className="flex flex-1 flex-col">{children}</div>
    </div>
  );
}

function CumulativeConsumptionSection({
  title,
  dateLabel,
  percent,
  amount,
}: {
  title: string;
  dateLabel: string;
  percent: string;
  amount: string;
}) {
  return (
    <div className="border-b border-border pb-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted">{title}</p>
      <p className="mt-1 text-xs text-muted">{dateLabel}</p>
      <div className="mt-2 flex items-end justify-between gap-3">
        <p className="text-xl font-bold text-green">{percent}</p>
        <p className="text-sm font-semibold text-primary">{amount}</p>
      </div>
    </div>
  );
}

function BreakdownRow({
  icon: Icon,
  label,
  value,
  iconClassName = "text-primary",
  valueClassName,
}: {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  value: string;
  iconClassName?: string;
  valueClassName?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="flex items-center gap-2 text-muted-foreground">
        <Icon size={15} className={iconClassName} />
        {label}
      </span>
      <span className={cn("font-semibold text-foreground", valueClassName)}>{value}</span>
    </div>
  );
}

export function EnterpriseDashboardPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.dashboard.enterpriseBoard;
  const [searchParams, setSearchParams] = useSearchParams();

  useEnterpriseDashboardAlerts();

  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const initialFilters = useMemo(
    () => parseDashboardPeriodSearchParams(searchParams),
    [searchParams],
  );

  const [dateFrom, setDateFrom] = useState(initialFilters.dateFrom);
  const [dateTo, setDateTo] = useState(initialFilters.dateTo || todayIso);
  const [hospital, setHospital] = useState(initialFilters.hospitalId);
  const [activeQuickAccess, setActiveQuickAccess] = useState("admissions");
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);

  useEffect(() => {
    setDateFrom(initialFilters.dateFrom);
    setDateTo(initialFilters.dateTo || todayIso);
    setHospital(initialFilters.hospitalId);
    setAppliedFilters(initialFilters);
  }, [initialFilters, todayIso]);

  const syncUrlFilters = useCallback(
    (filters: typeof appliedFilters) => {
      const qs = dashboardPeriodQueryString(filters);
      setSearchParams(qs ? new URLSearchParams(qs) : {}, { replace: true });
    },
    [setSearchParams],
  );

  const { data, hospitals, periodLabel, loading, error, usingApi } =
    useEnterpriseDashboard(appliedFilters);

  const analyticsPeriodQuery = dashboardPeriodQueryString(appliedFilters);
  const visitsDetailHref = analyticsPeriodQuery
    ? `/dashboard/enterprise/analytics/visits?${analyticsPeriodQuery}`
    : "/dashboard/enterprise/analytics/visits";
  const agePyramidDetailHref = analyticsPeriodQuery
    ? `/dashboard/enterprise/analytics/age-pyramid?${analyticsPeriodQuery}`
    : "/dashboard/enterprise/analytics/age-pyramid";

  const hospitalOptions = useMemo(
    () => [
      { value: "", label: labels.filters.allHospitals ?? "Tous les hôpitaux" },
      ...hospitals.map((row) => ({
        value: String(row.id),
        label: row.name,
      })),
    ],
    [hospitals, labels.filters.allHospitals],
  );

  const quickAccessItems = data.quickAccess.map((item) => ({
    ...item,
    label: labels.quickAccess[item.id],
  }));

  const careTypeRows = data.careTypes.map((row) => ({
    type: labels.careTypes.rows[row.type],
    quantity: row.quantity,
    price: row.price,
  }));

  const subtitle = useMemo(() => {
    if (usingApi && periodLabel && periodLabel !== "Historique complet") {
      const parts = [periodLabel];
      if (data.header.budgetHeader) {
        parts.push(`budget ${data.header.budgetHeader}`);
      } else {
        parts.push(`budget ${data.header.budgetLabel} USD — prévision ${data.header.forecastYear}`);
      }
      parts.push(`${data.header.beneficiaries} bénéficiaires`);
      if (appliedFilters.dateTo) {
        const end = new Date(`${appliedFilters.dateTo}T00:00:00`);
        if (!Number.isNaN(end.getTime())) {
          parts.push(`effectif au ${end.toLocaleDateString("fr-FR")}`);
        }
      }
      return parts.join(" · ");
    }

    return labels.subtitle
      .replace("{budget}", data.header.budgetLabel)
      .replace("{year}", data.header.forecastYear)
      .replace("{beneficiaries}", String(data.header.beneficiaries));
  }, [appliedFilters.dateTo, data.header, labels.subtitle, periodLabel, usingApi]);

  const cumulativeSection = (
    <CumulativeConsumptionSection
      title={labels.budget.cumulative}
      dateLabel={
        data.budget.cumulativeDate.includes("/")
          ? data.budget.cumulativeDate
          : labels.budget.cumulativeOn.replace("{date}", data.budget.cumulativeDate)
      }
      percent={data.budget.cumulativePercent}
      amount={data.budget.cumulativeAmount}
    />
  );

  return (
    <div className="space-y-5">
      <DashboardPageHeader
        title={labels.pageTitle}
        subtitle={subtitle}
        icon={LayoutDashboard}
        badge={data.header.companyBadge}
        date={data.header.displayDate}
      />

      <div className="header-ribbon filter-panel relative isolate rounded-[var(--radius-card)] p-5">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-muted">
              {labels.filters.from}
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
              className={`${inputClass} min-w-[160px]`}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-muted">
              {labels.filters.to}
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
              className={`${inputClass} min-w-[160px]`}
            />
          </div>
          <div className="min-w-[220px] flex-1">
            <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-muted">
              {labels.filters.hospital}
            </label>
            <select
              value={hospital}
              onChange={(event) => setHospital(event.target.value)}
              className={selectClass}
            >
              {hospitalOptions.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={() => {
              const next = { dateFrom, dateTo, hospitalId: hospital };
              setAppliedFilters(next);
              syncUrlFilters(next);
            }}
            disabled={loading}
            className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-70"
          >
            <Filter size={15} />
            {loading ? "Chargement…" : labels.filters.apply}
          </button>
          <button
            type="button"
            onClick={() => {
              setDateFrom("");
              setDateTo(todayIso);
              setHospital("");
              const cleared = { dateFrom: "", dateTo: "", hospitalId: "" };
              setAppliedFilters(cleared);
              syncUrlFilters(cleared);
            }}
            className="cursor-pointer rounded-lg border border-border bg-card px-4 py-2.5 text-xs font-semibold tracking-wide text-muted-foreground transition-colors hover:bg-card-muted"
          >
            {labels.filters.fullHistory}
          </button>
          {usingApi && periodLabel && (
            <span className="rounded-full bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary">
              {periodLabel}
            </span>
          )}
        </div>
        {error && (
          <p className="mt-4 rounded-xl border border-red-500/30 bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">
            {error}
          </p>
        )}
        {!usingApi && (
          <p className="mt-4 text-xs text-amber-700 dark:text-amber-300">
            Mode démo (données statiques). Connectez-vous pour charger les données API.
          </p>
        )}
        <p className="mt-4 text-xs leading-relaxed text-muted">{labels.filterDisclaimer}</p>
      </div>

      <div>
        <DashboardSectionLabel>{labels.sections.quickAccess}</DashboardSectionLabel>
        <EnterpriseDashboardQuickAccess
          items={quickAccessItems}
          activeId={activeQuickAccess}
          onSelect={setActiveQuickAccess}
        />
      </div>

      <div>
        <DashboardSectionLabel>{labels.sections.synthesis}</DashboardSectionLabel>
        <div className="grid grid-cols-1 items-stretch gap-4 xl:grid-cols-4">
          <SynthesisCard
            title={labels.budget.title}
            subtitle={
              <>
                <p>{data.budget.forecastLabel}</p>
                <p>{data.budget.forecastPeriodLabel}</p>
                {data.budget.allocationDetail ? (
                  <p className="text-[11px] leading-snug">{data.budget.allocationDetail}</p>
                ) : null}
              </>
            }
            primaryValue={data.budget.allocatedAmount}
            icon={Wallet}
            iconClassName="bg-emerald-500/15"
            iconColorClassName="text-emerald-600 dark:text-emerald-400"
          >
            <div className="mt-auto space-y-4 text-sm">
              {cumulativeSection}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted">
                  {labels.budget.outOfPackage}
                </p>
                <p className="mt-1 text-xs text-muted">
                  {data.budget.outOfPackageDate.includes("/")
                    ? data.budget.outOfPackageDate
                    : labels.budget.outOfPackageOn.replace("{date}", data.budget.outOfPackageDate)}
                </p>
                <div className="mt-2 flex items-end justify-between gap-3">
                  <p className="text-xl font-bold text-foreground">{data.budget.outOfPackagePercent}</p>
                  <p className="text-sm font-semibold text-red-600 dark:text-red-400">{data.budget.outOfPackageAmount}</p>
                </div>
              </div>
            </div>
          </SynthesisCard>

          <SynthesisCard
            title={labels.averageConsumption.title}
            subtitle={labels.averageConsumption.subtitle}
            primaryValue={data.averageConsumption.attendanceRate}
            primaryCaption={labels.averageConsumption.attendanceRate}
            icon={Clock3}
            iconClassName="bg-amber-500/15"
            iconColorClassName="text-amber-600 dark:text-amber-400"
          >
            <div className="mt-auto space-y-3">
              <BreakdownRow
                icon={DollarSign}
                label={labels.averageConsumption.amountPerAdmission}
                value={data.averageConsumption.amountPerAdmission}
                iconClassName="text-emerald-600 dark:text-emerald-400"
              />
              <BreakdownRow
                icon={DollarSign}
                label={labels.averageConsumption.amountPerAgent}
                value={data.averageConsumption.amountPerAgent}
                iconClassName="text-sky-600 dark:text-sky-400"
              />
              <BreakdownRow
                icon={Users}
                label={labels.averageConsumption.admittedAgents}
                value={data.averageConsumption.admittedAgents}
                iconClassName="text-violet-600 dark:text-violet-400"
              />
            </div>
          </SynthesisCard>

          <SynthesisCard
            title={labels.workforce.title}
            subtitle={labels.workforce.subtitle}
            primaryValue={data.workforce.total}
            primaryCaption={labels.workforce.total}
            icon={Users}
            iconClassName="bg-sky-500/15"
            iconColorClassName="text-sky-600 dark:text-sky-400"
          >
            <div className="mt-auto space-y-3">
              <BreakdownRow
                icon={Users}
                label={labels.workforce.agents}
                value={data.workforce.agents}
                iconClassName="text-sky-600 dark:text-sky-400"
              />
              <BreakdownRow
                icon={Heart}
                label={labels.workforce.spouses}
                value={data.workforce.spouses}
                iconClassName="text-rose-600 dark:text-rose-400"
              />
              <BreakdownRow
                icon={Smile}
                label={labels.workforce.children}
                value={data.workforce.children}
                iconClassName="text-amber-600 dark:text-amber-400"
              />
            </div>
          </SynthesisCard>

          <SynthesisCard
            title={labels.attendance.title}
            subtitle={labels.attendance.subtitle}
            primaryValue={data.attendance.total}
            primaryCaption={labels.attendance.total}
            icon={UserPlus}
            iconClassName="bg-violet-500/15"
            iconColorClassName="text-violet-600 dark:text-violet-400"
          >
            <div className="mt-auto space-y-3">
              <BreakdownRow
                icon={Users}
                label={labels.attendance.agents}
                value={data.attendance.agents}
                iconClassName="text-sky-600 dark:text-sky-400"
              />
              <BreakdownRow
                icon={Heart}
                label={labels.attendance.spouses}
                value={data.attendance.spouses}
                iconClassName="text-rose-600 dark:text-rose-400"
              />
              <BreakdownRow
                icon={Smile}
                label={labels.attendance.children}
                value={data.attendance.children}
                iconClassName="text-amber-600 dark:text-amber-400"
              />
            </div>
          </SynthesisCard>
        </div>
      </div>

      <div>
        <DashboardSectionLabel>{labels.sections.invoicesAdmissions}</DashboardSectionLabel>
        <EnterpriseDashboardMetrics labels={labels.metrics} data={data.metrics} />
      </div>

      <div>
        <DashboardSectionLabel>{labels.sections.statistics}</DashboardSectionLabel>
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <EnterpriseDashboardRankingCard
            title={labels.visitDistribution.title}
            icon={ChartPie}
            iconClassName="bg-primary/15 text-primary"
            viewAllLabel={labels.visitDistribution.viewAll}
            detailHref={visitsDetailHref}
            badgeClassName="bg-[#ebe9fe] text-[#6d5dd3]"
            columns={[
              { title: labels.visitDistribution.agents, entries: [...data.visitDistribution.agents] },
              { title: labels.visitDistribution.spouses, entries: [...data.visitDistribution.spouses] },
              { title: labels.visitDistribution.children, entries: [...data.visitDistribution.children] },
            ]}
          />
          <EnterpriseDashboardRankingCard
            title={labels.pathologies.title}
            icon={HeartPulse}
            iconClassName="bg-red-500/15 text-red-600 dark:text-red-400"
            badgeClassName="bg-teal-soft text-green"
            columns={[
              { title: labels.visitDistribution.agents, entries: [...data.frequentPathologies.agents] },
              { title: labels.visitDistribution.spouses, entries: [...data.frequentPathologies.spouses] },
              { title: labels.visitDistribution.children, entries: [...data.frequentPathologies.children] },
            ]}
          />
          <EnterpriseDashboardCareTypes
            title={labels.careTypes.title}
            subtitle={labels.careTypes.subtitle}
            columns={labels.careTypes.columns}
            rows={careTypeRows}
          />
          <EnterpriseDashboardAgePyramid
            title={labels.agePyramid.title}
            subtitle={labels.agePyramid.subtitle}
            viewAllLabel={labels.agePyramid.viewAll}
            detailHref={agePyramidDetailHref}
            data={[...data.agePyramid]}
          />
        </div>
      </div>
    </div>
  );
}
